import React from 'react';
import { useTranslation } from 'react-i18next';

import { ICONS_MAP } from 'constants/icons';
import { appReactMemo } from 'hocs';
import { useBreakpoint } from 'hooks/adaptive/useBreakpoint';
import { ThemedElement } from 'utils/types';

import Loader from '../Loader';

import { IButtonProps, StyledButton, StyledTooltip } from './styled';

const Button = React.forwardRef<HTMLButtonElement, ThemedElement<IButtonProps>>(
  (
    {
      children,
      className,
      closeTooltipIn = 300,
      copyText = '',
      enableCopyToClipboard,
      enableShowFocus,
      endIcon,
      hideCopyIcon,
      isActive,
      isLoading,
      loaderSize = 40,
      onClick,
      previewState,
      showTooltipOnClick = true,
      size = 'lg',
      startIcon,
      tooltipDisabled,
      tooltipText,
      variant,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const checkWindowWidth = useBreakpoint();

    const onCopy = React.useCallback(() => {
      navigator.clipboard.writeText(copyText);
    }, [copyText]);

    const getButtonSize = () => {
      return checkWindowWidth(900) && size === 'lg' ? 'md' : size;
    };

    const getStartIcon = () => {
      if (enableCopyToClipboard && !hideCopyIcon) {
        return <ICONS_MAP.Copy />;
      }
      if ((variant === 'icon' || variant === 'primaryIcon') && isLoading) {
        return;
      }
      return startIcon;
    };

    const getEndIcon = () => {
      if (variant === 'icon' && isLoading) {
        return;
      }
      return endIcon;
    };

    const button = (
      <StyledButton
        /*
          This happens because of @private/components and current project react types (node_modules/@types/react/index.d.ts)
          have differences. In @private/components types onResize and onResizeCapture defined. 
        */
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...(props as any)}
        $enableShowFocus={enableShowFocus}
        $isActive={isActive}
        $isLoading={isLoading}
        $previewState={previewState}
        className={className}
        endIcon={getEndIcon()}
        onClick={enableCopyToClipboard ? onCopy : onClick}
        ref={ref}
        size={getButtonSize()}
        startIcon={getStartIcon()}
        variant={variant}
      >
        {isLoading ? <Loader size={loaderSize} /> : children}
      </StyledButton>
    );

    if (enableCopyToClipboard) {
      return (
        <StyledTooltip
          closeIn={closeTooltipIn}
          component={t('common.copied')}
          disabled={tooltipDisabled}
          showOnClick={showTooltipOnClick}
        >
          {button}
        </StyledTooltip>
      );
    }

    if (tooltipText) {
      return (
        <StyledTooltip
          closeIn={closeTooltipIn}
          component={tooltipText}
          disabled={tooltipDisabled}
          showOnClick={showTooltipOnClick}
        >
          {button}
        </StyledTooltip>
      );
    }

    return button;
  },
);
Button.displayName = 'Button';

export default appReactMemo(Button);
